/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { TextAreaInputForm } from '../../inputs/forms/TextAreaInputForm';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface NotificationFormProps {
  defaultValues?: any;
  show?: boolean;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function NotificationForm({
  defaultValues = {},
  show = false,
}: NotificationFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/notifications';
  const { requestApi: reqApiNotifications, error: errorNotifications } = useFetch(baseUrl, {
    actionFeedback: false,
  });
  const {
    requestApi: reqApiInstitutions,
    data: dataInstitutions,
    loading: loadingInstitutions,
  } = useFetch('/v1/admin/institutions');
  const { data: optionsInstitutions, pagination: paginationInstitutions } =
    getFetchData(dataInstitutions);
  const {
    requestApi: reqApiTeams,
    data: dataTeams,
    loading: loadingTeams,
  } = useFetch('/v1/admin/teams');
  const { data: optionsTeams, pagination: paginationTeams } = getFetchData(dataTeams);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId } = useParams();
  const navigate = useNavigate();

  const optionsUserType = [
    {
      label: 'Todos',
      value: 'TODOS',
    },
    {
      label: 'Professor',
      value: 'PROFESSOR',
    },
    {
      label: 'Responsável',
      value: 'RESPONSAVEL',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Título da notificação',
      label: 'Título',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'description',
      value: defaultValues.description ?? '',
      placeholder: 'Descrição da notificação',
      label: 'Descrição',
      yup: Yup.string()
        .test('wordCount', 'A descrição deve ter no máximo 44 palavras.', (value) => {
          if (value) {
            const wordCount = value.split(' ').length;
            return wordCount <= 44;
          }
          return true;
        })
        .required('Este campo é obrigatório!'),
    },
    {
      name: 'user_type',
      value: defaultValues?.user_type
        ? optionsUserType.find((item) => item.value === defaultValues?.user_type)
        : null,
      placeholder: 'Grupo do usuário',
      label: 'Grupo',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'institution_id',
      value: defaultValues?.institution
        ? getFormattedLabel(defaultValues?.institution)
        : { value: null, label: 'Todas' },
      placeholder: 'Selecione uma instituição',
      label: 'Instituição',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'team_id',
      value: defaultValues?.team
        ? getFormattedLabel(defaultValues?.team)
        : { value: null, label: 'Todas' },
      placeholder: 'Selecione uma turma',
      label: 'Turma',
      yup: Yup.mixed(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const description = formInputs.find((field) => field.name === 'description');
  const userType = formInputs.find((field) => field.name === 'user_type');
  const institution = formInputs.find((field) => field.name === 'institution_id');
  const team = formInputs.find((field) => field.name === 'team_id');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      user_type: values.user_type.value,
      institution_id: values.institution_id.value,
      team_id: values.team_id?.value ?? null,
    };
    await reqApiNotifications(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage="notifications" mb={5} />
        <PageTitle>
          {show ? 'Visualizar' : defaultValues.name ? 'Editar' : 'Criar'} Notificação
        </PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!errorNotifications
              ? `Notificação ${defaultValues.name ? 'editada' : 'criada'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} a notificação.`}
          </Text>
        }
        msgStatus={errorNotifications ? 'error' : 'success'}
        closeOnOverlayClick={!!errorNotifications}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate('/notifications')}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>

        <Flex gap={5}>
          <Box flex={5}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled={show}
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              isDisabled={show}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        <Flex gap={5}>
          <Box flex={1}>
            <SelectInputForm
              name={userType?.name}
              label={userType?.label}
              options={optionsUserType}
              isRequired
              isDisabled={show}
              placeholder={userType?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SelectInputForm
              name={institution?.name}
              label={institution?.label}
              options={[
                { value: null, label: 'Todas' },
                ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
              ]}
              isLoading={loadingInstitutions}
              pagination={paginationInstitutions}
              getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
              isRequired
              isDisabled={show}
              placeholder={institution?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>

          {watch('institution_id')?.value && watch('institution_id')?.value !== 'TODAS' && (
            <Box flex={1}>
              <SelectInputForm
                name={team?.name}
                label={team?.label}
                options={[
                  { value: null, label: 'Todas' },
                  ...(optionsTeams?.map((item) => getFormattedLabel(item)) ?? []),
                ]}
                isLoading={loadingTeams}
                isDisabled={show}
                pagination={paginationTeams}
                getOptions={(filters) =>
                  reqApiTeams(
                    'get',
                    { params: { ...filters } },
                    `/v1/admin/teams?institution_id=${watch('institution_id').value}`
                  )
                }
                placeholder={team?.placeholder}
                control={control}
                errors={errors}
              />
            </Box>
          )}
        </Flex>

        <Box flex={1}>
          <TextAreaInputForm
            name={description?.name}
            label={description?.label}
            placeholder={description?.placeholder}
            isRequired
            isDisabled={show}
            errors={errors}
            register={register}
          />
        </Box>
        {show ? undefined : (
          <Flex mr="auto">
            <FormButton
              mt={4}
              w="100px"
              isLoading={isSubmitting}
              type="submit"
              textTransform="uppercase"
            >
              Salvar
            </FormButton>
          </Flex>
        )}
      </form>
    </FormBox>
  );
}
